import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Container } from '@radix-ui/themes'

import useVehicleFetcher from '../hooks/useVehicleFetcher'
import Vehicle from '../interfaces/Vehicle'
import Client from '../interfaces/Client'
import useClientFetcher from '../hooks/useClientFetcher'
import useSalenoteVoid from '../hooks/useSalenoteVoid'
import useSalenoteApprove from '../hooks/useSalenoteApprove'
import { useAuth } from '../hooks/useAuth'
import { MessageCrea } from '../utils/utils'
import { AppPath } from '../enums/AppPath'
import { NvvAction } from '../enums/DocActions'
import useSalenoteFetcher from '../hooks/useSalenoteFetcher'
import { Salenote } from '../interfaces/Salenote'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { chaConsumablesWarning } from '../utils/chaProducts'

import { Alert, Info } from './MessageInfo'
import SalenoteEdit from './SalenoteEdit'
import { ButtonAction } from './Buttons'

const SalenoteDetailPage: React.FC<{
  salenoteId?: string
  onClose?: (approve: boolean) => void
}> = ({ salenoteId, onClose }) => {
  const { useNewDocumentUI } = useFeatureFlag()
  // Extracting id parameter from the route
  const { id } = useParams<{ id: string }>()
  const [refreshKey, setRefreshKey] = useState(0) // A key to trigger re-fetching
  const { errorSalenote, fetchSalenote } = useSalenoteFetcher()
  const [salenote, setSalenote] = useState<Salenote>()
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const [vehicle, setVehicle] = useState<Vehicle>()
  const { errorClient, fetchClient } = useClientFetcher()
  const [client, setClient] = useState<Client>()
  const { errorSalenoteVoid, loading: loadingVoid, salenoteVoid } = useSalenoteVoid()
  const { errorSalenoteApprove, loading: loadingApprove, salenoteApprove } = useSalenoteApprove()
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { userData } = useAuth()
  const idx = salenoteId ?? id ?? ''

  useEffect(() => {
    fetchDataAsync(idx)
  }, [idx, refreshKey])

  const fetchDataAsync = async (idAux: string) => {
    if (!idx) {
      return // Exit early if `id` is undefined
    }

    const salenote = await fetchSalenote(idAux)
    const doc = 'nota de venta'

    if (errorSalenote) {
      setErrorMessage(errorSalenote.message)

      return
    }

    if (!salenote) {
      setErrorMessage(`Error recuperando la ${doc}`)

      return
    }

    // El vehiculo
    const vehicleId = salenote.vehicle
    const vehicle = await fetchVehicle(vehicleId)

    if (errorVehicle) {
      setErrorMessage(errorVehicle.message)

      return
    }

    if (!vehicle) {
      setErrorMessage('Error recuperando el vehículo')

      return
    }
    const clientId = salenote.client
    const client = await fetchClient(clientId)

    if (errorClient) {
      setErrorMessage(errorClient.message)

      return
    }

    if (!client) {
      setErrorMessage('Error recuperando el cliente')

      return
    }
    setClient(client)
    setVehicle(vehicle)
    setSalenote(salenote)
  }

  const alertMessageTimeout = (): void => {
    setErrorMessage('')
  }

  const infoMessageTimeout = (): void => {
    setInfoMessage('')
  }

  const handleAnularNvv = async (motivo: string) => {
    if (!salenote || !userData) return
    const id = salenote._id
    const by = userData.profile?._id ?? ''
    const res = await salenoteVoid(id, motivo, by)

    // refrescamos la pantalla
    if (res === null) {
      return
    }

    const msg = MessageCrea(AppPath.SaleNotes, NvvAction.Anular)

    setInfoMessage(msg)
    handleRefresh()
  }

  const handleReassigned = () => {
    setInfoMessage('Nota de venta actualizada: se cambió el vendedor o el aprobador.')
    handleRefresh()
  }

  const handleAprobarNvv = async () => {
    if (!salenote || !userData) return
    const id = salenote._id
    const by = userData.profile?._id ?? ''
    const res = await salenoteApprove(id, by)

    // refrescamos la pantalla
    if (res === null) {
      return
    }

    const msg = MessageCrea(AppPath.SaleNotes, NvvAction.Aprobar)

    setInfoMessage(msg)
    handleRefresh()

    if (onClose) {
      onClose(true)
    }
  }
  const handleRefresh = () => setRefreshKey((prev) => prev + 1) // Increment the refreshKey to trigger re-fetch

  return (
    <Container size={useNewDocumentUI ? '4' : '3'}>
      <div>{errorSalenoteVoid?.message}</div>
      <div>{errorSalenoteApprove?.message}</div>
      {errorMessage !== '' && <Alert message={errorMessage} trigger={Date.now()} onTimeout={alertMessageTimeout} />}
      {infoMessage !== '' && <Info message={infoMessage} trigger={Date.now()} onTimeout={infoMessageTimeout} />}
      {/* Aprobar vende el vehículo y lo despublica: los destacados activos se pierden. */}
      {salenote && vehicle && !salenote.approvedAt && !salenote.voidedAt && chaConsumablesWarning(vehicle) && (
        <div className="my-2 rounded-md border border-yellow-400 bg-yellow-50 p-3 text-sm text-yellow-800">
          ⚠️ {chaConsumablesWarning(vehicle)} Al aprobar la venta el aviso se despublica.
        </div>
      )}
      {salenote && vehicle && client && (
        <SalenoteEdit
          // SalenoteEdit arma su formulario una sola vez al montarse. Si cambia el vendedor
          // (salenote_reassign) se remonta con la nota ya recargada, o la vista previa
          // seguiría mostrando al vendedor anterior.
          key={salenote.profile}
          vehicle={vehicle}
          client={client}
          salenote={salenote}
          handleAnularNvv={handleAnularNvv}
          handleAprobarNvv={handleAprobarNvv}
          handleReassigned={handleReassigned}
          onRefresh={handleRefresh}
          loadingApprove={loadingApprove}
          loadingVoid={loadingVoid}
        />
      )}
      {onClose && salenote && (
        <div className="flex justify-center">
          <ButtonAction label="Volver al negocio" handleButtonClick={() => onClose(false)} />
        </div>
      )}
    </Container>
  )
}
export default SalenoteDetailPage
